class AmPmVideoGallery extends HTMLElement {
  connectedCallback() {
    this._currentIndex = 0;
    this._isMuted = true;
    this._isPlaying = false;
    this._isMobile = false;
    this._autoTimer = null;
    this._trackedVideo = null;
    this._onTimeUpdate = null;

    this._stageEl = this.querySelector('.am-pm-video-gallery__stage');
    this._thumbnailsEl = this.querySelector('.am-pm-video-gallery__thumbnails');
    this._timelineFill = this.querySelector('.am-pm-video-gallery__timeline-fill');
    this._pauseBtn = this.querySelector('.am-pm-video-gallery__pause-btn');
    this._muteBtn = this.querySelector('.am-pm-video-gallery__mute-btn');

    this._updateView();
    this._resizeHandler = () => this._updateView();
    window.addEventListener('resize', this._resizeHandler, { passive: true });

    this._bindControls();
    this._bindTouchControls();
    this._watchModeChange();

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        observer.disconnect();
        this._init();
      }
    }, { rootMargin: '200px' });
    observer.observe(this);
  }

  disconnectedCallback() {
    clearInterval(this._autoTimer);
    clearTimeout(this._controlsHideTimer);
    this._stopTracking();
    window.removeEventListener('resize', this._resizeHandler);
    document.removeEventListener('ampm:mode-change', this._modeHandler);
    document.removeEventListener('touchstart', this._outsideTouchHandler);
  }

  _updateView() {
    this._isMobile = window.innerWidth < 991;
    this.classList.toggle('is-mobile-view', this._isMobile);
  }

  get _currentMode() {
    return document.body.classList.contains('ampm-mode-pm') ? 'pm' : 'am';
  }

  get _visibleSlides() {
    const mode = this._currentMode;
    return [...this.querySelectorAll('.am-pm-video-gallery__slide')].filter(
      (el) => el.dataset.showOnBoth === 'true' || el.dataset.blockType === `${mode}-slide`
    );
  }

  get _visibleThumbs() {
    const mode = this._currentMode;
    return [...this.querySelectorAll('.am-pm-video-gallery__thumb')].filter(
      (el) => el.dataset.showOnBoth === 'true' || el.dataset.blockType === `${mode}-slide`
    );
  }

  _init() {
    this.querySelectorAll('video').forEach((v) => v.pause());
    this._stopTracking();
    clearInterval(this._autoTimer);

    const allSlides = [...this.querySelectorAll('.am-pm-video-gallery__slide')];
    const allThumbs = [...this.querySelectorAll('.am-pm-video-gallery__thumb')];

    allSlides.forEach((el) => { el.classList.remove('is-active'); el.classList.add('is-mode-hidden'); });
    allThumbs.forEach((el) => { el.classList.remove('is-active'); el.classList.add('is-mode-hidden'); });

    this._visibleSlides.forEach((el) => el.classList.remove('is-mode-hidden'));
    this._visibleThumbs.forEach((el) => el.classList.remove('is-mode-hidden'));

    this._currentIndex = 0;
    this._goTo(0);
  }

  _getVideo(slide) {
    if (!slide) return null;
    const preferClass = this._isMobile
      ? 'video.am-pm-video-gallery__media--mobile'
      : 'video.am-pm-video-gallery__media--desktop';
    return slide.querySelector(preferClass) ?? slide.querySelector('video');
  }

  _goTo(index) {
    const slides = this._visibleSlides;
    const thumbs = this._visibleThumbs;
    if (!slides.length) return;

    const safeIndex = Math.max(0, Math.min(index, slides.length - 1));

    // Only pause previous slide if actually navigating away from it
    if (this._currentIndex !== safeIndex) {
      slides[this._currentIndex]?.querySelectorAll('video').forEach((v) => { v.pause(); v.currentTime = 0; });
    }

    this._stopTracking();
    clearInterval(this._autoTimer);
    if (this._timelineFill) this._timelineFill.style.width = '0%';

    this._currentIndex = safeIndex;
    slides.forEach((s, i) => s.classList.toggle('is-active', i === safeIndex));
    thumbs.forEach((t, i) => t.classList.toggle('is-active', i === safeIndex));

    const activeSlide = slides[safeIndex];
    const videoAttr = this._isMobile ? 'mobVideo' : 'deskVideo';
    const hasVideo = activeSlide?.dataset[videoAttr] === 'true';

    this.classList.toggle('is-image-slide', !hasVideo);

    if (hasVideo) {
      const video = this._getVideo(activeSlide);
      if (video) {
        video.muted = this._isMuted;
        if (video.readyState === 0) { video.preload = 'auto'; video.load(); }
        const p = video.play();
        if (p !== undefined) {
          p.then(() => {
            this._isPlaying = true;
            this._updateState();
            this._startTracking(video);
          }).catch(() => {
            this._isPlaying = false;
            this._updateState();
          });
        } else {
          this._isPlaying = true;
          this._updateState();
          this._startTracking(video);
        }
      }
    } else {
      this._isPlaying = true;
      this._updateState();
      this._startImageTimer();
    }
  }

  _startTracking(video) {
    this._trackedVideo = video;
    this._onTimeUpdate = () => {
      if (!video.duration) return;
      const pct = (video.currentTime / video.duration) * 100;
      if (this._timelineFill) this._timelineFill.style.width = `${pct}%`;
    };
    this._onVideoEnded = () => {
      const slides = this._visibleSlides;
      this._goTo((this._currentIndex + 1) % slides.length);
    };
    video.addEventListener('timeupdate', this._onTimeUpdate);
    video.addEventListener('ended', this._onVideoEnded);
  }

  _stopTracking() {
    if (this._trackedVideo && this._onTimeUpdate) {
      this._trackedVideo.removeEventListener('timeupdate', this._onTimeUpdate);
    }
    if (this._trackedVideo && this._onVideoEnded) {
      this._trackedVideo.removeEventListener('ended', this._onVideoEnded);
    }
    this._trackedVideo = null;
    this._onTimeUpdate = null;
    this._onVideoEnded = null;
  }

  _startImageTimer() {
    const slides = this._visibleSlides;
    if (slides.length <= 1) return;
    this._autoTimer = setInterval(() => {
      this._goTo((this._currentIndex + 1) % slides.length);
    }, parseInt(this.dataset.imageDuration, 10) || 4000);
  }

  _togglePlayPause() {
    const activeSlide = this._visibleSlides[this._currentIndex];
    const hasVideo = activeSlide?.dataset.deskVideo === 'true' || activeSlide?.dataset.mobVideo === 'true';
    const video = hasVideo ? this._getVideo(activeSlide) : null;

    if (video) {
      if (video.paused) {
        video.play().then(() => {
          this._isPlaying = true;
          this._updateState();
          this._startTracking(video);
        }).catch(() => {});
      } else {
        video.pause();
        this._stopTracking();
        this._isPlaying = false;
        this._updateState();
      }
    } else {
      if (this._isPlaying) {
        clearInterval(this._autoTimer);
        this._isPlaying = false;
      } else {
        this._isPlaying = true;
        this._startImageTimer();
      }
      this._updateState();
    }
  }

  _toggleMute() {
    this._isMuted = !this._isMuted;
    this.querySelectorAll('video').forEach((v) => { v.muted = this._isMuted; });
    this._updateState();
  }

  _updateState() {
    this.classList.toggle('is-playing', this._isPlaying);
    this.classList.toggle('is-unmuted', !this._isMuted);
  }

  _bindControls() {
    this._thumbnailsEl?.addEventListener('click', (e) => {
      const thumb = e.target.closest('.am-pm-video-gallery__thumb');
      if (!thumb) return;
      const index = this._visibleThumbs.indexOf(thumb);
      if (index !== -1) this._goTo(index);
    });
    this._pauseBtn?.addEventListener('click', () => this._togglePlayPause());
    this._muteBtn?.addEventListener('click', () => this._toggleMute());

  }

  _watchModeChange() {
    this._modeHandler = () => this._init();
    document.addEventListener('ampm:mode-change', this._modeHandler);
  }

  _bindTouchControls() {
    if (!this._stageEl) return;

    const CONTROLS_IDLE_DELAY = 3000;

    this._showControls = () => {
      clearTimeout(this._controlsHideTimer);
      this._stageEl.classList.add('is-controls-visible');
      this._controlsHideTimer = setTimeout(() => {
        this._stageEl.classList.remove('is-controls-visible');
      }, CONTROLS_IDLE_DELAY);
    };

    this._stageEl.addEventListener('touchstart', this._showControls, { passive: true });

    this._outsideTouchHandler = (e) => {
      if (!this._stageEl.contains(e.target)) {
        clearTimeout(this._controlsHideTimer);
        this._stageEl.classList.remove('is-controls-visible');
      }
    };
    document.addEventListener('touchstart', this._outsideTouchHandler, { passive: true });
  }
}

if (!customElements.get('am-pm-video-gallery')) {
  customElements.define('am-pm-video-gallery', AmPmVideoGallery);
}
